/* IMPORT */
import { OWNER } from '../objects/superroot.js';
import { lazySetAdd, lazySetDelete } from '../lazy.js';
import cleanup from './cleanup.js';
import resolve from './resolve.js';
import { frozen, readable } from '../objects/callable.js';
import Observable from '../objects/observable.js';
import Root from '../objects/root.js';
import { SYMBOL_CACHED, SYMBOL_SUSPENSE, SYMBOL_UNCACHED } from '../symbols.js';
import { callStack } from './debugger.js';
/* HELPERS */
const DUMMY_INDEX = frozen(-1);
class MappedRoot extends Root {
}
/* MAIN */
class CacheKeyed {
    /* CONSTRUCTOR */
    constructor(fn) {
        /* VARIABLES */
        this.parent = OWNER;
        this.suspense = OWNER.get(SYMBOL_SUSPENSE);
        this.cache = new Map();
        this.bool = false; // The bool is flipped with each iteration, the roots that don't have the updated one are disposed, it's like a cheap counter basically
        this.prevCount = 0; // Number of previous items
        this.reuseCount = 0; // Number of previous items that got reused
        this.nextCount = 0; // Number of next items
        /* API */
        this.cleanup = () => {
            if (!this.prevCount)
                return; // There was nothing before, no need to cleanup
            if (this.prevCount === this.reuseCount)
                return; // All the previous items were reused, no need to cleanup
            const { cache, bool } = this;
            if (!cache.size)
                return; // Nothing to dispose of
            if (this.nextCount) { // Regular cleanup
                cache.forEach((mapped, value) => {
                    if (mapped.bool === bool)
                        return;
                    mapped.dispose(true);
                    cache.delete(value);
                });
            }
            else { // There is nothing after, disposing quickly
                this.cache.forEach(mapped => {
                    mapped.dispose(true);
                });
                this.cache = new Map();
            }
        };
        this.dispose = () => {
            if (this.suspense) {
                lazySetDelete(this.parent, 'roots', this.roots);
            }
            this.prevCount = this.cache.size;
            this.reuseCount = 0;
            this.nextCount = 0;
            this.cleanup();
        };
        this.before = () => {
            this.bool = !this.bool;
            this.reuseCount = 0;
            this.nextCount = 0;
        };
        this.after = (values) => {
            this.nextCount = values.length;
            this.cleanup();
            this.prevCount = this.nextCount;
            this.reuseCount = 0;
        };
        this.map = (values) => {
            this.before();
            const { cache, bool, fn, fnWithIndex } = this;
            const results = new Array(values.length);
            let resultsCached = true; // Whether all results are cached, if so this enables an optimization
            let resultsUncached = true; // Whether all results are anew, if so this enables an optimization in Voby
            let reuseCount = 0;
            const stack = callStack();
            for (let i = 0, l = values.length; i < l; i++) {
                const value = values[i];
                const cached = cache.get(value);
                if (cached && cached.bool !== bool) {
                    resultsUncached = false;
                    reuseCount += 1;
                    cached.bool = bool;
                    cached.index?.set(i);
                    results[i] = cached.result; //TSC
                }
                else {
                    resultsCached = false;
                    const mapped = new MappedRoot(false);
                    if (cached) {
                        cleanup(() => mapped.dispose(true));
                    }
                    mapped.wrap(() => {
                        let index = DUMMY_INDEX;
                        if (fnWithIndex) {
                            mapped.index = new Observable(i);
                            index = readable(mapped.index, stack);
                        }
                        const result = results[i] = resolve(fn(value, index));
                        mapped.bool = bool;
                        mapped.result = result;
                        if (!cached) {
                            cache.set(value, mapped);
                        }
                    }, undefined, undefined, stack);
                }
            }
            this.reuseCount = reuseCount;
            this.after(values);
            if (resultsCached) {
                results[SYMBOL_CACHED] = true;
            }
            if (resultsUncached) {
                results[SYMBOL_UNCACHED] = true;
            }
            return results;
        };
        this.roots = () => {
            return Array.from(this.cache.values());
        };
        this.fn = fn;
        this.fnWithIndex = (fn.length > 1);
        if (this.suspense) {
            lazySetAdd(this.parent, 'roots', this.roots);
        }
    }
}
/* EXPORT */
export default CacheKeyed;
